// Venue notifications for guests (Web Push).
//
// A guest taps "Get updates"; the browser asks permission, hands us an anonymous
// address from Apple / Google / Mozilla's push service, and we store it against
// this venue only. Messages then arrive even when the site is closed.
//
// iPhone and iPad (iOS 16.4+) only allow this for a site that was added to the
// Home Screen, and the permission prompt must follow a tap inside that app.
// Android works straight from the browser.

const API = () => process.env.REACT_APP_API_BASE_URL;
const SW_URL = () => `${process.env.PUBLIC_URL || ''}/push-sw.js`;

export const isIOS = (nav = window.navigator) => /iphone|ipad|ipod/i.test(nav.userAgent || '') ||
  // iPadOS reports itself as a Mac with a touch screen
  (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);

export const isStandalone = (win = window) =>
  win.navigator.standalone === true ||
  Boolean(win.matchMedia && win.matchMedia('(display-mode: standalone)').matches);

/** What this device can do right now, without asking for anything. */
export function pushCapability(win = window) {
  const hasApis = 'serviceWorker' in win.navigator && 'PushManager' in win && 'Notification' in win;
  if (isIOS(win.navigator) && !isStandalone(win)) {
    // In a normal Safari tab these APIs are absent; the guest has to install first.
    return { state: 'needs-install' };
  }
  if (!hasApis) return { state: 'unsupported' };
  if (win.Notification.permission === 'denied') return { state: 'blocked' };
  return { state: 'ready' };
}

export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** true when the key is unknown (older browsers) or equal to the server's key. */
export function sameKey(existing, expected) {
  if (!existing) return true;
  const a = new Uint8Array(existing);
  if (a.length !== expected.length) return false;
  return a.every((value, i) => value === expected[i]);
}

const REQUEST_TIMEOUT = 15000;
const preferenceVersions = new Map();
const preferenceIntents = new Map();
const enabledVenues = new Map();
const versionKey = venueId => `push_preference_version_${venueId}`;
const intentKey = venueId => `push_preference_intent_${venueId}`;
let transportQueue = Promise.resolve();

function latestIntent(venueId) {
  let latest = preferenceIntents.get(venueId);
  try {
    const saved = JSON.parse(localStorage.getItem(intentKey(venueId)) || 'null');
    if (saved && Number.isSafeInteger(saved.version) && typeof saved.subscribed === 'boolean'
      && (!latest || saved.version >= latest.version)) latest = saved;
  } catch { /* Use this page's last intent when browser storage is unavailable. */ }
  return latest;
}

function reserveIntent(venueId, subscribed) {
  const version = nextOperationVersion(venueId);
  const intent = { version: Number(version), subscribed };
  preferenceIntents.set(venueId, intent);
  try { localStorage.setItem(intentKey(venueId), JSON.stringify(intent)); } catch { /* Retain the in-memory intent. */ }
  return version;
}

async function withTransportLock(task) {
  // All venues share this browser transport. Serialize replacement and opt-out across tabs.
  if (navigator.locks?.request) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    try {
      return await navigator.locks.request('venue-push-transport', { signal: controller.signal }, () => {
        clearTimeout(timer);
        return task();
      });
    } finally {
      clearTimeout(timer);
    }
  }
  const pending = transportQueue.then(task);
  transportQueue = pending.catch(() => {});
  return pending;
}

async function keepLatestIntent(venueId, operationVersion, sub) {
  const latest = latestIntent(venueId);
  if (!latest || latest.version <= Number(operationVersion)) return true;
  if (!latest.subscribed && sub) {
    // A later opt-out may have seen no subscription while this one was being created.
    await postJson(`/public/push/${encodeURIComponent(venueId)}/unsubscribe`, {
      endpoint: sub.endpoint, operationVersion: String(latest.version), protocolVersion: 2,
    });
  }
  return false;
}

function rememberedVersion(venueId) {
  let saved = 0;
  try { saved = Number(localStorage.getItem(versionKey(venueId))) || 0; } catch { /* Memory still orders this page's operations. */ }
  return Math.max(preferenceVersions.get(venueId) || 0, Number.isSafeInteger(saved) && saved > 0 ? saved : 0);
}

function rememberVersion(venueId, raw) {
  const value = Number(raw);
  if (!Number.isSafeInteger(value) || value < 0) return;
  const latest = Math.max(rememberedVersion(venueId), value);
  preferenceVersions.set(venueId, latest);
  try { localStorage.setItem(versionKey(venueId), String(latest)); } catch { /* Keep the in-memory counter. */ }
}

function nextOperationVersion(venueId) {
  const version = Math.max(Date.now() * 1000, rememberedVersion(venueId) + 1);
  if (!Number.isSafeInteger(version)) throw new Error('Cannot create a notification preference version.');
  rememberVersion(venueId, version);
  return String(version);
}

async function withTimeout(task) {
  let timer;
  try {
    return await Promise.race([
      task,
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Notification request timed out.')), REQUEST_TIMEOUT); }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

async function postJson(path, body) {
  const res = await withTimeout(fetch(`${API()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }));
  let data = null;
  try {
    data = await withTimeout(res.json());
  } catch (e) {
    data = null;
  }
  if (!res.ok) {
    const error = new Error((data && data.error) || `Request failed (${res.status})`);
    error.status = res.status;
    throw error;
  }
  if (!data || typeof data !== 'object') throw new Error('Invalid notification response.');
  return data;
}

async function registration() {
  // Always register our handler, even when an older worker already owns the scope.
  await withTimeout(navigator.serviceWorker.register(SW_URL()));
  return withTimeout(navigator.serviceWorker.ready);
}

/** false when the server has notifications switched off for this venue; null when we cannot tell. */
export async function isPushEnabledFor(venueId) {
  enabledVenues.delete(venueId);
  try {
    const res = await withTimeout(fetch(`${API()}/public/push/${encodeURIComponent(venueId)}/enabled`));
    if (!res.ok) return null;
    const data = await withTimeout(res.json());
    const enabled = typeof data?.enabled === 'boolean' ? data.enabled : null;
    enabledVenues.set(venueId, enabled);
    return enabled;
  } catch (e) {
    return null;
  }
}

/** The browser's current subscription, or null. Never prompts. */
export async function currentSubscription() {
  if (!('serviceWorker' in navigator)) return null;
  const reg = await withTimeout(navigator.serviceWorker.getRegistration('/'));
  return reg ? withTimeout(reg.pushManager.getSubscription()) : null;
}

/** true / false, or null when we cannot tell (no subscription, offline). */
export async function isSubscribedTo(venueId) {
  try {
    const sub = await currentSubscription();
    if (!sub) return false;
    const data = await postJson(`/public/push/${encodeURIComponent(venueId)}/status`, { endpoint: sub.endpoint });
    rememberVersion(venueId, data.operationVersion);
    return Boolean(data.subscribed);
  } catch (e) {
    return null;
  }
}

/** Must be called from a tap. Resolves to { ok: true } or { ok: false, reason }. */
export async function enableNotifications(venueId) {
  try {
    // refresh() checks eligibility before exposing the button. Do not introduce an await
    // before requestPermission(): iOS requires it to run directly from the guest's tap.
    if (enabledVenues.get(venueId) !== true) return { ok: false, reason: 'unavailable' };
    const cap = pushCapability();
    if (cap.state !== 'ready') return { ok: false, reason: cap.state };
    // Reserve before the first await: an old slow operation keeps its original version.
    const operationVersion = reserveIntent(venueId, true);
    let permission = window.Notification.permission;
    // Keep permission prompting directly in the tap handler before any network await.
    if (permission === 'default') permission = await window.Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, reason: permission === 'denied' ? 'blocked' : 'dismissed' };

    return await withTransportLock(async () => {
      if (!(await keepLatestIntent(venueId, operationVersion, null))) return { ok: false, reason: 'unavailable' };
      const response = await withTimeout(fetch(`${API()}/public/push/key`));
      if (!response.ok) return { ok: false, reason: 'unavailable' };
      const { key } = await withTimeout(response.json());
      if (!key) return { ok: false, reason: 'unavailable' };

      const reg = await registration();
      const serverKey = urlBase64ToUint8Array(key);
      let sub = await withTimeout(reg.pushManager.getSubscription());
      // Replace subscriptions made with a different VAPID key while holding the transport lock.
      if (sub && !sameKey(sub.options && sub.options.applicationServerKey, serverKey)) {
        await withTimeout(sub.unsubscribe());
        sub = null;
      }
      if (!sub) {
        sub = await withTimeout(reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: serverKey }));
      }
      if (!(await keepLatestIntent(venueId, operationVersion, sub))) return { ok: false, reason: 'unavailable' };
      await postJson(`/public/push/${encodeURIComponent(venueId)}/subscribe`, { subscription: sub.toJSON(), operationVersion, protocolVersion: 2 });
      if (!(await keepLatestIntent(venueId, operationVersion, sub))) return { ok: false, reason: 'unavailable' };
      return { ok: true };
    });
  } catch (error) {
    return { ok: false, reason: 'unavailable' };
  }
}

/** Stops this venue only. The browser subscription is shared across venues and tabs. */
export async function disableNotifications(venueId) {
  const operationVersion = reserveIntent(venueId, false);
  return withTransportLock(async () => {
    const sub = await currentSubscription();
    if (!sub) return { ok: true };
    const stopped = new Set();
    let current = sub;
    // Clients without Web Locks can still replace the endpoint while this request runs.
    for (let attempt = 0; current && !stopped.has(current.endpoint) && attempt < 3; attempt += 1) {
      stopped.add(current.endpoint);
      await postJson(`/public/push/${encodeURIComponent(venueId)}/unsubscribe`, { endpoint: current.endpoint, operationVersion, protocolVersion: 2 });
      current = await currentSubscription();
    }
    if (current && !stopped.has(current.endpoint)) throw new Error('Notification subscription is still changing. Please check its status.');
    // Keep the shared transport; only this venue's links are removed.
    return { ok: true };
  });
}
